import type { MutableRefObject } from 'react'

import { init as initFrontmanClient } from '@tmap-web-lib/remote-api-client/frontman'
import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { TmapLog } from '@tmap-web-lib/tmap-log-client'
import { getSearchParams } from '@tmap-web-lib/utils'
import { useAtom, useSetAtom } from 'jotai'
import { toLower } from 'lodash-es'
import { PropsWithChildren, createContext, useCallback, useEffect, useRef } from 'react'
import { coerce, gte } from 'semver'
import { UAParser } from 'ua-parser-js'

import { createOnAccessKeyExpired } from '@/Providers/access-key'
import { pageIdInfos } from '@/Providers/page-id-context'
import { initMockClient } from '@/apis'
import { globalState, storageAccessKeyAtom } from '@/atoms'
import { frontmanState } from '@/atoms/frontmanState'
import { initMoengage, setUserAttrs, setUserId } from '@/libs/moengage'
import { logError, rum } from '@/utils/rum'

const NEXT_PUBLIC_BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV as string
const TARGET_BUILD_ENV = process.env.NODE_ENV === 'production' ? NEXT_PUBLIC_BUILD_ENV : 'local'
const ua = UAParser()

const sentErrorUrls = new Set<string>()
const sentErrorCodes = new Set<string>()

const AccessKeyRefContext = createContext<MutableRefObject<string> | null>(null)

type TmapInfo = {
  deviceId?: string
  carrier?: string
  euk?: string
  sessionId?: string
}

/**
 * iOS에서 window.tmapInfo가 나중에 세팅되는 타이밍 이슈 대응용 polling
 * timeout: 최대 3000ms, interval: 200ms
 */
const waitForWindowTmapInfo = (timeout = 3000, interval = 200): Promise<TmapInfo | null> => {
  return new Promise((resolve) => {
    const start = Date.now()

    const timer = setInterval(() => {
      const info = (window as any).tmapInfo as TmapInfo | undefined
      if (info && info.deviceId) {
        clearInterval(timer)
        resolve(info)
      } else if (Date.now() - start > timeout) {
        clearInterval(timer)
        resolve(null)
      }
    }, interval)
  })
}

const getAccessKeyWithRetry = async (options?: {
  retries?: number
  interval?: number
}): Promise<string> => {
  const { retries = 3, interval = 200 } = options || {}

  for (let i = 0; i < retries; i++) {
    try {
      const key = await TmapApp.getAccessKey()
      if (key) return key as string
    } catch (_e) {}

    if (i < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
  }

  return ''
}

/**
 * TmapApp.getTmapInfo()를 3번 재시도 + iOS window.tmapInfo 보완
 */
const getDeviceIdWithRetry = async (options?: {
  retries?: number
  interval?: number
}): Promise<string | null> => {
  const { retries = 3, interval = 200 } = options || {}

  for (let i = 0; i < retries; i++) {
    try {
      const deviceId = await TmapApp.getDeviceId()
      if (deviceId) return deviceId

      if (TmapApp.env.isIOS && TmapApp.env.isInApp) {
        const infoFromWindow = (window as any).tmapInfo as TmapInfo | undefined
        if (infoFromWindow?.deviceId) {
          return infoFromWindow.deviceId
        }
      }
    } catch (e) {
      if (!sentErrorCodes.has('my:error:tmapInfo:retry')) {
        sentErrorCodes.add('my:error:tmapInfo:retry')
        logError('my:error:tmapInfo:retry', { error: e })
      }
    }

    if (i < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
  }

  if (TmapApp.env.isIOS && TmapApp.env.isInApp) {
    const info = await waitForWindowTmapInfo(3000, 200)
    if (info?.deviceId) return info.deviceId
  }

  return null
}

const InitializationProvider = ({ children }: PropsWithChildren<unknown>) => {
  const setGlobalStateValue = useSetAtom(globalState)
  const [accessKeyStorageValue, setAccessKeyStorage] = useAtom(storageAccessKeyAtom)
  const setFrontman = useSetAtom(frontmanState)
  const accessKeyRef = useRef('')
  const accessKeyStorageRef = useRef(accessKeyStorageValue as string)
  
  const init = useCallback(async () => {
    try {
      setFrontman({ isInitialized: false, hasError: false })

      const [serverType, originDeviceId, accessKey, carrierName, euk, sessionId] =
        await Promise.all([
          TmapApp.getUserSetting({ key: 'local.server_type' }),
          getDeviceIdWithRetry({ retries: 3, interval: 200 }),
          getAccessKeyWithRetry({ retries: 3, interval: 200 }),
          TmapApp.getCarrierName(),
          TmapApp.getEUK(),
          TmapApp.getSessionId(),
          TmapApp.getRedDotList(),
        ])

      const isPrd = process.env.NEXT_PUBLIC_BUILD_ENV === 'PRD' && serverType !== 'rtg'
      if (serverType === 'rtg') {
        const rawAppVersion = TmapApp.env?.appVersion
        const normalizedAppVersion = coerce(rawAppVersion)?.version ?? '0.0.0'
        const path = gte(normalizedAppVersion, '11.0.0') ? '/web/my' : '/web'

        if (
          process.env.NEXT_PUBLIC_TARGET === 'prd' &&
          process.env.NEXT_PUBLIC_BUILD_ENV !== 'RTG'
        ) {
          window.location.replace(`https://tmobi-my-rtg.tmapmobility.com${path}`)
          return
        } else if (
          process.env.NEXT_PUBLIC_TARGET === 'stg' &&
          process.env.NEXT_PUBLIC_BUILD_ENV !== 'DTG'
        ) {
          window.location.replace(`https://tmobi-my-dtg.tmapmobility.com${path}`)
          return
        }
      }
      const infoFromWindow = (window as any).tmapInfo as TmapInfo | undefined

      let deviceId = originDeviceId || infoFromWindow?.deviceId

      if (!deviceId) {
        if (TmapApp.env.isInApp) {
          logError('my:error:deviceInfo:invalid', { originDeviceId })
          setFrontman({ isInitialized: false, hasError: true })
          return
        }

        deviceId = 'WEB_DEVICE'
      }

      const reg = /^[a-zA-Z\d]*$/
      const carrier = reg.test(carrierName ?? '') ? carrierName : 'ETC'
      const ak = accessKey || getSearchParams().ak || accessKeyStorageRef.current || ''
      if (ak) {
        accessKeyRef.current = ak as string
        setAccessKeyStorage(ak)
        setGlobalStateValue((prev) => ({
          ...prev,
          accessKey: typeof ak === 'object' ? JSON.stringify(ak) : ak,
          deviceId: deviceId as string,
          carrierName: carrier as string,
        }))
      } else {
        setGlobalStateValue((prev) => ({
          ...prev,
          deviceId: deviceId as string,
          carrierName: carrier as string,
        }))
      }

      initFrontmanClient({
        hostUrl: process.env.NEXT_PUBLIC_FRONTMAN_URL,
        headers: {
          Carrier: carrier ?? '',
          'Device-Id': deviceId ?? '',
          'App-Code': 'TMAP',
          'Os-Type': TmapApp.env.isAndroid ? 'AND' : 'IOS',
          'App-Version': TmapApp.env.isInApp ? TmapApp.env.appVersion : '11.0.0',
          'Os-Version': ua.os.version || '',
          'Model-No': ua.device.model || '',
          Using: 'WEB_MY',
          Euk: euk,
        },
        timeout: 10000,
        disableHMAC: true,
        onRequest(config) {
          config.headers.AccessKey = accessKeyRef.current
          if (TARGET_BUILD_ENV !== 'PRD') {
            rum.addAction(`[RemoteApi][Request] ${config.url}`, {
              params: config.params,
              headers: config.headers,
              body: config.headers.getContentType()?.includes('multipart') ? 'skip' : config.data
            })
          }
          return config
        },
        onResponse(response) {
          if (TARGET_BUILD_ENV !== 'PRD') {
            rum.addAction(`[RemoteApi][Response] ${response.config.url}`, {
              headers: response.headers,
              body: response.data,
            })
          }
          return response
        },
        onAccessKeyChanged(newAccessKey: string) {
          if (newAccessKey) {
            accessKeyRef.current = newAccessKey
            TmapApp.updateAccessKey({ key: newAccessKey })
            setAccessKeyStorage(newAccessKey)
            setGlobalStateValue((prev) => ({
              ...prev,
              accessKey: newAccessKey,
            }))
          }
        },
        onAccessKeyExpired: createOnAccessKeyExpired({
          accessKeyRef,
          setAccessKeyStorage,
          setGlobalStateValue,
        }),
        onError(error) {
          if (TARGET_BUILD_ENV !== 'PRD') {
            rum.addAction(`[RemoteApi][Error] ${error?.response?.config.url}`, {
              headers: error?.response?.headers,
              body: error?.response?.data,
            })
            logError(error instanceof Error ? error : String(error))
          }
          if (error.code !== 'ECONNABORTED') {
            const url = error?.response?.config?.url ?? 'unknown'
            if (!sentErrorUrls.has(url)) {
              sentErrorUrls.add(url)
              logError('my:error:frontman:onError', { error, url })
            }
          }
        },
      })

      initMockClient({
        onRequest(config) {
          config.headers.AccessKey = accessKeyRef.current
          return config
        },
        onResponse(response) {
          return response
        },
        onAccessKeyChanged(newAccessKey: string) {
          if (newAccessKey) {
            accessKeyRef.current = newAccessKey
            TmapApp.updateAccessKey({ key: newAccessKey })
            setAccessKeyStorage(newAccessKey)
            setGlobalStateValue((prev) => ({
              ...prev,
              accessKey: newAccessKey,
            }))
          }
        },
        onAccessKeyExpired(e) {
          console.log('onMockAPIAccessKeyExpired === ', e)
        },
        onError(e) {
          console.log('onMockAPIError', e)
        },
      })

      const logServiceId = (() => {
        const href = window.location.href
        if (href.includes('/step_counter')) {
          return 'pedometer'
        }
        if (href.includes(pageIdInfos.move_log.url) || href.includes(pageIdInfos.move_log_insight.url)) {
          return 'movinglog'
        }
        if (href.includes(pageIdInfos.vertical_poi_search.url)) {
          return 'vertical_poi_search'
        }
        return 'my'
      })()
      TmapLog.init({
        appId: 'tmap',
        serviceId: logServiceId,
        appVersion: TmapApp.env.appVersion,
        carrier: carrier,
        deviceId: deviceId,
        sessionId: sessionId,
        logbox: {
          env: isPrd ? 'PRD' : 'STG',
          token: isPrd ? 'F5CD39ECE530' : '3AF19605D63E',
          logVersion: `1.0-${toLower(process.env.NEXT_PUBLIC_BUILD_ENV)}`,
        },
        mixpanel: {
          token: process.env.NEXT_PUBLIC_LOG_COLLECTOR_TOKEN ?? '',
          api_host: process.env.NEXT_PUBLIC_LOG_COLLECTOR_URL ?? '',
          api_method: 'POST',
          api_payload_format: 'base64',
          identity: euk,
          debug: !isPrd,
        },
        onSend: (tmapLogBuilder) => {
          tmapLogBuilder
            .setPageType('WEB')
            .setCustom('deploy_env', process.env.NEXT_PUBLIC_BUILD_ENV)
            .setEuk(euk)
        },
      })

      // MoEngage 로그
      initMoengage({
        appId: isPrd ? 'LLV7LBWWWP4W834AJB10POF2' : '8L4VR4B4VHSX4ZU83ZCZ2EPD',
        cluster: 'dc_1',
      })
      
      setUserId(euk).then(() => {
        setUserAttrs({
          deviceId,
          carrier,
          sessionId,
          buildEnv: process.env.NEXT_PUBLIC_BUILD_ENV,
        })
      })

      rum.init(deviceId)

      setFrontman({ isInitialized: true, hasError: false })
    } catch (e) {
      logError('my:error:init', { error: e })
      if (TmapApp.env.isInApp) {
        setFrontman({ isInitialized: false, hasError: true })
      } else {
        setFrontman({ isInitialized: true, hasError: false })
      }
    }
  }, [setFrontman, setAccessKeyStorage, setGlobalStateValue])

  useEffect(() => {
    accessKeyStorageRef.current = accessKeyStorageValue as string
  }, [accessKeyStorageValue])

  useEffect(() => {
    init()
  }, [init])

  return <AccessKeyRefContext.Provider value={accessKeyRef}>{children}</AccessKeyRefContext.Provider>
}

export { InitializationProvider, AccessKeyRefContext }
